import dynamic from "next/dynamic";
import LazyLoaded from "../LazyLoaded";
import { Paperclip, Pause, Play } from "lucide-react";
import { spacing } from "@/utils/spacing";
import { Editor } from "@knowt/editor/react";
import { FlexRowAlignCenter } from "../Flex";
import Skeleton from "@mui/material/Skeleton";
import { CodeXml, Spline, X } from "lucide-react";
import { themeColors } from "@/utils/themeColors";
import { borderRadius } from "@/utils/borderRadius";
import { Mic, Radical, Square } from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { LocalMessageAttachment } from "@knowt/syncing/types/common";
import {
    fileTypeFromMimeType,
    generalizedMediaTypeFromSingleFileType,
    SUPPORTED_CHAT_ATTACHMENT_MIME_TYPES,
} from "@knowt/syncing/utils/fileTypeUtils";
import CircularRectTextButton from "../CircularButton/styled/CircularRectTextButton";
import { Attachment, MediaType } from "@knowt/syncing/graphql/schema";
import { capitalize } from "@knowt/syncing/utils/stringUtils";
import LinkWrapper from "../wrappers/Link";
import ZoomableImage from "../ZoomableImage";
import useAudioRecorder from "@/features/AudioRecording/useAudioRecorder";
import { AudioVisualizer } from "@/features/AudioRecording/AudioVisualizer";
import CircularFilledIcon from "../CircularButton/styled/CircularFilledIcon";
import { useS3UploadJobsSelector } from "@knowt/syncing/context/S3UploadJobContext";
import Mountable from "../styled/div/Mountable";
import { useCurrentUser } from "@knowt/syncing/hooks/user/useCurrentUser";
import { assertTruthy } from "@knowt/syncing/utils/assertions";

const WhiteboardPopup = dynamic(() => import("@/components/Tldraw/WhiteBoardPopup"), { ssr: false });
const MathFieldInputPopup = dynamic(() => import("@/components/MathField/MathFieldInputPopup"), { ssr: false });

const LiveAudioVisualizer = React.lazy(async () => {
    const { LiveAudioVisualizer } = await import("@/features/AudioRecording/LiveAudioVisualizer");
    return { default: LiveAudioVisualizer };
});

const ChatEditorToolBarButton = ({
    id,
    icon,
    onClick,
    children,
    buttonStyle,
}: {
    id: string;
    icon?: React.ReactNode;
    onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
    children?: React.ReactNode;
    buttonStyle?: React.CSSProperties;
}) => {
    return (
        <CircularRectTextButton
            id={id}
            style={{ width: "3rem", height: "3rem", borderRadius: borderRadius.circular, ...buttonStyle }}
            onClick={onClick}>
            {children || icon}
        </CircularRectTextButton>
    );
};

type RichEditorResponseToolbarProps = {
    editor: Editor | null;
    localEditorAttachments: LocalMessageAttachment[] | null;
    handleUpdateAttachments?: (attachments: LocalMessageAttachment[] | null) => void | null;
    buttonStyle?: React.CSSProperties;
    confirmFileUpload?: ({
        file,
        name,
        extension,
        customBucketName,
        ignoreMediaCreation,
    }: {
        file: File;
        name: string;
        extension?: string;
        customBucketName: string;
        ignoreMediaCreation: boolean;
    }) => Promise<{ id: string; extension: string }>;
    filesS3BucketName?: string;
    stretchToolbar?: (isFullWidth: boolean) => void;
};

export const RichEditorResponseToolbar = ({
    editor,
    localEditorAttachments,
    handleUpdateAttachments,
    filesS3BucketName,
    buttonStyle,
    stretchToolbar,
}: RichEditorResponseToolbarProps) => {
    const { userId } = useCurrentUser();
    const [isWhiteBoardOpen, setIsWhiteBoardOpen] = useState(false);
    const [isMathFieldInputPopupOpen, setIsMathFieldInputPopupOpen] = useState(false);
    const [selectedLatex, setSelectedLatex] = useState<string | null>(null);

    const confirmFileUpload = useS3UploadJobsSelector(s => s.confirmUpload);
    const isDisabled = useS3UploadJobsSelector(s => s.isDisabled);
    const [isRecordingAudio, setIsRecordingAudio] = useState(false);

    const [isRecordingComplete, setIsRecordingComplete] = useState(false);

    const fileInputRef = useRef<HTMLInputElement>(null);

    const recorder = useAudioRecorder({
        noiseSuppression: true,
        echoCancellation: true,
    });

    const { mediaRecorder, recordingBlob } = recorder;

    const handleAddCodeBlock = useCallback(
        (e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            editor?.commands.insertContent({ type: "paragraph" });
            editor?.chain().focus().toggleCodeBlock().run();
        },
        [editor]
    );

    const handleAddMathBlock = useCallback(
        (e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            setSelectedLatex(null);
            if (editor?.state?.selection) {
                const { from, to } = editor.state.selection;
                const text = editor.state.doc.textBetween(from, to).trim();

                if (text.startsWith("$$") && text.endsWith("$$")) {
                    // Extract latex between $$ markers
                    const latex = text.slice(2, -2).trim();
                    setSelectedLatex(latex);
                }
            }
            setIsMathFieldInputPopupOpen(true);
        },
        [editor]
    );

    const handleInsertMathBlock = useCallback(
        (value: string) => {
            editor?.commands.insertContent(`$$${value}$$ `); // we need to add a space after the math block to display it properly directly after addition
            setIsMathFieldInputPopupOpen(false);
            setSelectedLatex(null);
            editor?.commands.focus();
        },
        [editor]
    );

    const handleAddWhiteboardToLocalAttachments = useCallback(
        (whiteboardAttachment: LocalMessageAttachment) => {
            const newAttachments = [...(localEditorAttachments || []), whiteboardAttachment];
            assertTruthy(handleUpdateAttachments, "handleUpdateAttachments is required");
            handleUpdateAttachments(newAttachments);
            setIsWhiteBoardOpen(false);
        },
        [localEditorAttachments, handleUpdateAttachments]
    );

    const handleFileInputChange = useCallback(
        async (e: React.ChangeEvent<HTMLInputElement>) => {
            const file = e.target.files?.[0];
            if (!file) return;

            const { id, bucket, extension } = await confirmFileUpload({
                file,
                name: file.name,
                userId,
                ignoreMediaCreation: true,
            });

            const newAttachments = [
                ...(localEditorAttachments || []),
                {
                    id,
                    name: file.name,
                    type: generalizedMediaTypeFromSingleFileType(extension),
                    url: `https://${bucket}.s3.amazonaws.com/${id}.${fileTypeFromMimeType(file.type)}`,
                },
            ];

            assertTruthy(handleUpdateAttachments, "handleUpdateAttachments is required");

            handleUpdateAttachments(newAttachments);
        },
        [confirmFileUpload, filesS3BucketName, localEditorAttachments, handleUpdateAttachments]
    );

    const handleAudioInputClick = () => {
        setIsRecordingAudio(true);
        stretchToolbar?.(true);
        recorder.startRecording();
    };

    const handleStopAudioRecording = () => {
        recorder.stopRecording();
        setIsRecordingAudio(false);
        stretchToolbar?.(false);
    };

    const handleConfirmAudioRecording = async () => {
        setIsRecordingComplete(true);
        handleStopAudioRecording();
    };

    useEffect(() => {
        const handleConfirmAudioRecording = async () => {
            if (recordingBlob && isRecordingComplete) {
                const audioUrl = await confirmFileUpload({
                    file: new File([recordingBlob], "audio.mp3", { type: "audio/mp3" }),
                    name: "audio.mp3",
                    extension: "mp3",
                    customBucketName: filesS3BucketName,
                    ignoreMediaCreation: true,
                });
                const newAttachments = [
                    ...(localEditorAttachments || []),
                    {
                        id: audioUrl.id,
                        name: "audio.mp3",
                        type: MediaType.AUDIO,
                        url: `https://${filesS3BucketName}.s3.amazonaws.com/${audioUrl.id}.mp3`,
                    },
                ];
                handleUpdateAttachments(newAttachments);
                setIsRecordingComplete(false);
            }
        };

        handleConfirmAudioRecording();
    }, [recordingBlob, isRecordingComplete]);

    if (isRecordingAudio) {
        return (
            <FlexRowAlignCenter style={{ width: "100%", justifyContent: "space-between", gap: spacing.SM }}>
                <ChatEditorToolBarButton
                    id="cancel-audio-input-button"
                    icon={<X size={18} />}
                    onClick={handleStopAudioRecording}
                />
                {mediaRecorder && (
                    <FlexRowAlignCenter style={{ width: "100%" }}>
                        <LiveAudioVisualizer
                            mediaRecorder={mediaRecorder}
                            barWidth={3}
                            gap={2}
                            width={300}
                            height={12}
                            fftSize={128}
                            maxDecibels={-10}
                            minDecibels={-80}
                            barColor={themeColors.neutral3}
                            smoothingTimeConstant={0.8}
                            canvasStyle={{ width: "100%" }}
                        />
                    </FlexRowAlignCenter>
                )}
                <ChatEditorToolBarButton
                    id="stop-audio-input-button"
                    icon={<Square size={18} fill={themeColors.pureWhite} color={themeColors.pureWhite} />}
                    onClick={handleConfirmAudioRecording}
                    style={{ backgroundColor: themeColors.errorPrimary }}
                />
            </FlexRowAlignCenter>
        );
    }

    return (
        <>
            <Mountable mount={!isDisabled}>
                <ChatEditorToolBarButton
                    id="file-attachment-button"
                    onClick={() => fileInputRef.current?.click()}
                    buttonStyle={buttonStyle}>
                    <input
                        type="file"
                        ref={fileInputRef}
                        accept={SUPPORTED_CHAT_ATTACHMENT_MIME_TYPES.join(",")} // image and audio files
                        style={{ display: "none" }}
                        onChange={handleFileInputChange}
                        multiple={false}
                    />
                    <Paperclip size={18} />
                </ChatEditorToolBarButton>
            </Mountable>
            <ChatEditorToolBarButton
                id="code-block-button"
                icon={<CodeXml size={18} />}
                onClick={handleAddCodeBlock}
                buttonStyle={buttonStyle}
            />
            <ChatEditorToolBarButton
                id="math-block-button"
                icon={<Radical size={18} />}
                onClick={handleAddMathBlock}
                buttonStyle={buttonStyle}
            />
            <Mountable mount={!isDisabled}>
                <ChatEditorToolBarButton
                    id="whiteboard-button"
                    icon={<Spline size={18} />}
                    onClick={() => setIsWhiteBoardOpen(true)}
                    buttonStyle={buttonStyle}
                />
                <ChatEditorToolBarButton
                    id="audio-input-button"
                    icon={<Mic size={18} />}
                    onClick={handleAudioInputClick}
                    buttonStyle={buttonStyle}
                />
            </Mountable>
            <LazyLoaded load={isWhiteBoardOpen}>
                <WhiteboardPopup
                    isOpen={isWhiteBoardOpen}
                    onClose={() => setIsWhiteBoardOpen(false)}
                    onWhiteboardUsageConfirm={handleAddWhiteboardToLocalAttachments}
                />
            </LazyLoaded>
            <LazyLoaded load={isMathFieldInputPopupOpen}>
                <MathFieldInputPopup
                    isOpen={isMathFieldInputPopupOpen}
                    onClose={() => setIsMathFieldInputPopupOpen(false)}
                    onInsert={handleInsertMathBlock}
                    selectedLatex={selectedLatex}
                />
            </LazyLoaded>
        </>
    );
};

const CashedMessageAttachmentCard = ({
    attachment,
    attachmentTagStyle,
}: {
    attachment: Attachment;
    attachmentTagStyle?: React.CSSProperties;
}) => {
    return (
        <LinkWrapper
            href={attachment.url}
            openInNewTab
            style={{
                gap: spacing.SM,
                padding: `${spacing.XS} ${spacing.SM}`,
                borderRadius: "1.2rem",
                ...attachmentTagStyle,
            }}>
            <FlexRowAlignCenter style={{ gap: spacing.XS }}>
                <Paperclip size={18} />
                {capitalize(attachment.type)}
            </FlexRowAlignCenter>
        </LinkWrapper>
    );
};

const AttachmentCard = ({
    readOnly,
    attachment,
    onWhiteBoardCardClick,
    handleRemoveAttachment,
    onImageClick,
    attachmentTagStyle,
}: {
    readOnly?: boolean;
    attachment: LocalMessageAttachment;
    onWhiteBoardCardClick: (id: string) => void;
    handleRemoveAttachment: (id: string) => void;
    onImageClick: (imageUrl: string) => void;
    attachmentTagStyle?: React.CSSProperties;
}) => {
    const playBackAudioRef = useRef<HTMLAudioElement | null>(null);
    const [isAudioPlaying, setIsAudioPlaying] = useState(false);
    const [blob, setBlob] = useState<Blob | null>(null);
    const handleRemoveAttachmentClicked = (e: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
        e.stopPropagation();
        handleRemoveAttachment(attachment.id);
    };

    const getAudioBlob = async (audioUrl: string) => {
        const response = await fetch(audioUrl);
        if (!response.ok) {
            throw new Error("Failed to fetch the audio file");
        }
        const blob = await response.blob();
        return blob;
    };

    const handlePlayAudio = async () => {
        // biome-ignore lint/complexity/noForEach: <explanation>
        Array.from(document.querySelectorAll("audio")).forEach(audio => {
            if (audio !== playBackAudioRef.current) {
                audio.pause();
                audio.currentTime = 0;
            }
        });

        if (isAudioPlaying) {
            setIsAudioPlaying(false);
            playBackAudioRef.current?.pause();
            return;
        }
        const audioElement = playBackAudioRef.current;
        if (!audioElement) return;

        // Remove any existing ended event listeners before adding a new one
        audioElement.removeEventListener("ended", () => setIsAudioPlaying(false));
        audioElement.removeEventListener("pause", () => setIsAudioPlaying(false));

        audioElement.addEventListener("ended", () => setIsAudioPlaying(false));
        audioElement.addEventListener("pause", () => setIsAudioPlaying(false));

        if (!blob || !playBackAudioRef.current) return;

        setIsAudioPlaying(true);
        playBackAudioRef.current.src = URL.createObjectURL(blob);
        playBackAudioRef.current.play();
    };

    const getAttachmentIconByType = (): JSX.Element => {
        const iconSize = 18;

        switch (true) {
            case Boolean(attachment.imageUrl):
                return <Spline size={iconSize} />;
            case attachment.type === MediaType.AUDIO:
                return isAudioPlaying ? <Pause size={iconSize} /> : <Play size={iconSize} />;
            default:
                return <Paperclip size={iconSize} />;
        }
    };

    const handleAttachmentCardClick = () => {
        if (attachment.imageUrl) {
            // then it's a whiteboard and image url represents the whiteboard screenshot
            return onWhiteBoardCardClick(attachment.id);
        }

        if (attachment.type === MediaType.IMAGE) {
            return onImageClick(attachment.url);
        }

        if (attachment.type === MediaType.AUDIO) {
            return handlePlayAudio();
        }

        window.open(attachment.url, "_blank");
    };

    const renderCardBody = () => {
        if (attachment.type === MediaType.AUDIO) {
            return blob ? (
                <AudioVisualizer
                    gap={3}
                    blob={blob}
                    width={100}
                    height={30}
                    barWidth={4}
                    barColor={themeColors.neutralBlack}
                    style={{
                        width: "100%",
                        color: themeColors.neutralBlack,
                    }}
                />
            ) : (
                <div style={{ width: "10rem", height: "0.2rem", backgroundColor: themeColors.neutral2 }} />
            );
        }
        return attachment.imageUrl ? attachment.name : capitalize(attachment.type);
    };

    useEffect(() => {
        if (attachment.type !== MediaType.AUDIO) return;

        const fetchAudioBlob = async () => {
            const fetchedBlob = await getAudioBlob(attachment.url);
            setBlob(fetchedBlob);
        };
        fetchAudioBlob();
    }, [attachment.url, attachment.type]);

    return (
        <>
            <CircularRectTextButton
                style={{
                    gap: spacing.SM,
                    padding: `${spacing.XS} ${spacing.SM}`,
                    borderRadius: "1.2rem",
                    ...attachmentTagStyle,
                }}
                onClick={handleAttachmentCardClick}>
                <FlexRowAlignCenter style={{ gap: spacing.XS }}>
                    {getAttachmentIconByType()}
                    {renderCardBody()}
                </FlexRowAlignCenter>
                {!readOnly && (
                    <CircularFilledIcon
                        Icon={X}
                        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                            e.stopPropagation();
                            handleRemoveAttachmentClicked(e as unknown as React.MouseEvent<SVGSVGElement>);
                        }}
                        primaryColor={themeColors.neutralBlack}
                        IconProps={{ size: 18 }}
                        radius={"3rem"}
                    />
                )}
            </CircularRectTextButton>
            {attachment.type === MediaType.AUDIO && (
                <audio
                    ref={playBackAudioRef}
                    controls
                    controlsList="nodownload nofullscreen noremoteplayback noplaybackrate novolume"
                    src={attachment.url}
                    style={{ backgroundColor: "transparent", borderRadius: "14px", display: "none" }}>
                    <track kind="captions" />
                </audio>
            )}
        </>
    );
};

export const RichEditorAttachmentList = ({
    className,
    messageAttachments,
    localEditorAttachments,
    handleUpdateAttachments,
    readOnly,
    attachmentTagStyle,
    style,
}: {
    className?: string;
    readOnly?: boolean;
    messageAttachments?: Attachment[] | null;
    localEditorAttachments?: LocalMessageAttachment[] | null;
    handleUpdateAttachments?: (attachments: LocalMessageAttachment[] | null) => void;
    style?: React.CSSProperties;
    attachmentTagStyle?: React.CSSProperties;
}) => {
    const [isWhiteBoardOpen, setIsWhiteBoardOpen] = useState(false);
    const [selectedWhiteBoardId, setSelectedWhiteBoardId] = useState<string | null>(null);
    const [selectedImageUrl, setSelectedImageUrl] = useState<string | null>(null);
    const [isImageViewerOpen, setIsImageViewerOpen] = useState(false);

    const isUploading = useS3UploadJobsSelector(s => s.isUploading);

    if ((!localEditorAttachments || !localEditorAttachments.length) && !isUploading && !messageAttachments) return null;

    const handleOnWhiteBoardCardClick = (id: string) => {
        setSelectedWhiteBoardId(id);
        setIsWhiteBoardOpen(true);
    };

    const handleRemoveAttachment = (id: string) => {
        handleUpdateAttachments?.(localEditorAttachments?.filter(a => a.id !== id) || null);
    };

    const handleReplaceWhiteboardAttachment = (whiteboardAttachment: LocalMessageAttachment) => {
        handleUpdateAttachments?.(
            localEditorAttachments?.map(a => (a.id === whiteboardAttachment.id ? whiteboardAttachment : a)) || null
        );
        setIsWhiteBoardOpen(false);
    };

    const handleOpenImageViewer = (imageUrl: string) => {
        setSelectedImageUrl(imageUrl);
        setIsImageViewerOpen(true);
    };

    return (
        <>
            <FlexRowAlignCenter
                className={className}
                style={{
                    gap: spacing.XS,
                    flexWrap: "wrap",
                    width: "100%",
                    overflowX: "hidden",
                    ...style,
                }}>
                {messageAttachments?.map(attachment => (
                    <CashedMessageAttachmentCard
                        key={attachment.url}
                        attachment={attachment}
                        attachmentTagStyle={attachmentTagStyle}
                    />
                ))}
                {localEditorAttachments?.map(attachment => (
                    <AttachmentCard
                        key={attachment.id}
                        readOnly={readOnly}
                        attachment={attachment}
                        onWhiteBoardCardClick={handleOnWhiteBoardCardClick}
                        onImageClick={handleOpenImageViewer}
                        handleRemoveAttachment={handleRemoveAttachment}
                        attachmentTagStyle={attachmentTagStyle}
                    />
                ))}
                {isUploading && (
                    <Skeleton
                        className="secondaryTextBold1"
                        variant="rectangular"
                        width={150}
                        height={35}
                        style={{
                            borderRadius: "0 0 1.2rem 0",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}>
                        Uploading file...
                    </Skeleton>
                )}
            </FlexRowAlignCenter>
            <LazyLoaded load={isWhiteBoardOpen}>
                <WhiteboardPopup
                    isOpen={isWhiteBoardOpen}
                    onClose={() => setIsWhiteBoardOpen(false)}
                    defaultWhiteboardId={selectedWhiteBoardId}
                    onWhiteboardUsageConfirm={handleReplaceWhiteboardAttachment}
                    readOnly={readOnly}
                />
            </LazyLoaded>
            <LazyLoaded load={isImageViewerOpen && !!selectedImageUrl}>
                <ZoomableImage
                    isOpen={isImageViewerOpen}
                    onClose={() => setIsImageViewerOpen(false)}
                    img={selectedImageUrl as string}
                    alt={`attachment image`}
                />
            </LazyLoaded>
        </>
    );
};
